import { defineComponent, computed, ref, reactive, onMounted } from 'vue';
import { storeToRefs } from 'pinia';
import type { FormInstance } from 'element-plus';
import type { CountryForm } from '@/models/cashier-settings/add-country-dialog.model';
import type { CountryItem } from '@/models/dictionary/country-list.model';
import { CashierSettingsButtonName } from '@/models/cashier-settings/cashier-settings.model';
import { useCountryListStore } from '@/store/dictionary/country-list.store';
import { cashierSettingsValidationRules } from '@/hooks/cashier-settings/cashier-settings-validation-rules';
import { useCashierSettingsStore } from '@/store/cashier-settings/cashier-settings.store';

export default defineComponent({
  name: 'AddCountryDialog',
  props: {
    modelValue: {
      type: Boolean,
      required: true,
    },
  },
  emits: ['update:modelValue'],
  setup(_, { emit }) {
    const cashierSettingsStore = useCashierSettingsStore();
    const { cashierConfigList, isCreateCashierConfigLoading } = storeToRefs(cashierSettingsStore);
    const { createCashierConfig, setSelectedCountry } = cashierSettingsStore;

    const { getCountryList } = useCountryListStore();
    const { countryList } = storeToRefs(useCountryListStore());

    const addCountryForm = ref<FormInstance>();

    const modalForm = reactive<CountryForm>({
      country: '',
    });

    const availableCountryList = computed<CountryItem[]>(() => {
      const existedCountryList = cashierConfigList.value.map(config => config.country);

      return countryList.value
        .filter(country => !existedCountryList.some(existedCountry => country.code === existedCountry));
    });

    const isAvailableCountryList = computed<boolean>(() => Boolean(availableCountryList.value.length));

    const closeAddCountryDialog = (): void => {
      emit('update:modelValue', false);
    };

    const addCountry = (form?: FormInstance): void => {
      if (!form) return;

      form.validate(isValid => {
        if (!isValid) return;
        const countryName =
          availableCountryList.value.find(country => country.code === modalForm.country)?.name ?? '';

        createCashierConfig(modalForm.country, countryName)
          .then(() => {
            setSelectedCountry(modalForm.country);
            form.resetFields();
            closeAddCountryDialog();
          });
      });
    };

    onMounted(() => {
      getCountryList();
    });

    return {
      modalForm,
      addCountry,
      addCountryForm,
      availableCountryList,
      isAvailableCountryList,
      closeAddCountryDialog,
      CashierSettingsButtonName,
      cashierSettingsValidationRules,
      isCreateCashierConfigLoading,
    };
  },
});
